import {useCallback, useEffect, useRef, useState, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {Trans, useTranslation} from 'react-i18next';
import {
  CreateGuestByParticipantPayload,
  CreateGuestByParticipantResponse,
} from '@chekin/api';
import {FastAPIServiceError, Participant} from '@chekin/core';
import {StatusModalStates} from '@chekin/shared';
import {FailedMultipleSaveResponse} from 'pages/Auth/types';
import {OnboardingState, useOnboardingStore} from 'pages/Auth/store/useOnboardingStore';
import {PATHS} from 'routes/constants';
import {useReservation} from 'context/reservation';
import {useMutateMultipleGuests} from './useMutateMultipleGuests';
import {usePath} from 'hooks';
import {buildGuestInstantPayload, checkGuestsCompletionStatus} from 'pages/Auth/utils';
import {usePostHogTrack} from 'analytics';
import {POSTHOG_EVENTS} from 'analytics/posthog';
import {useModalControls} from '@chekin/guestapp-ui-kit';

const ON_SUCCESS_DELAY = 3000;

type UseGuestCreationProps = {
  participants?: Participant[];
  guestsData: OnboardingState['guestsData'];
  leaderGuestId?: string;
};

type CreateGuestsArgs = {
  customLeaderGuestId?: string;
  customParticipants?: Participant[];
  guestsData?: OnboardingState['guestsData'];
};

const parseErrorMessages = (
  errors: FailedMultipleSaveResponse<
    Omit<CreateGuestByParticipantPayload, 'reservation_id'>,
    FastAPIServiceError
  >[],
): string[] => {
  const messages: string[] = [];

  errors.forEach(errorItem => {
    const {item, error} = errorItem;
    const guestName = (item.full_name || '').trim() || 'Unknown guest';

    if (error.errors && error.errors.length > 0) {
      error.errors.forEach(fieldError => {
        if (fieldError.message) {
          messages.push(`${guestName}: ${fieldError.message}`);
        }
      });
    } else if (error.message) {
      messages.push(`${guestName}: ${error.message}`);
    } else if (error.detail) {
      messages.push(`${guestName}: ${error.detail}`);
    }
  });

  return messages;
};

export const useGuestCreation = ({
  participants,
  guestsData: storedGuestsData,
  leaderGuestId,
}: UseGuestCreationProps) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {data: reservation} = useReservation();
  const {trackEvent} = usePostHogTrack();
  const {isOpen, openModal, closeModal} = useModalControls();
  const {setParticipants} = useOnboardingStore();
  const [totalRegisteredGuests, setTotalRegisteredGuests] = useState(0);

  const {getFullPath} = usePath();
  const processTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [guestRegistrationStatus, setGuestRegistrationStatus] = useState(
    StatusModalStates.IDLE,
  );
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const handleOnSuccessSaveMultipleGuests = useCallback(
    (data: CreateGuestByParticipantResponse[]) => {
      setGuestRegistrationStatus(StatusModalStates.SUCCESS);
      setErrorMessages([]);
      setParticipants([]);
      setTotalRegisteredGuests(data.length);
      trackEvent(POSTHOG_EVENTS.INSTANT_CHECK_IN_COMPLETED, {
        guests_created_count: data.length,
      });
      const {hasIncompleteGuests} = checkGuestsCompletionStatus(data);
      const state = {hasIncompleteGuests};
      processTimeoutRef.current = setTimeout(() => {
        navigate(getFullPath(PATHS.guestRegistration), {
          state,
        });
      }, ON_SUCCESS_DELAY);
    },
    [navigate, getFullPath, setParticipants, trackEvent],
  );

  const handleOnErrorSaveMultipleGuests = useCallback(
    (
      error: FailedMultipleSaveResponse<
        Omit<CreateGuestByParticipantPayload, 'reservation_id'>,
        FastAPIServiceError
      >[],
    ) => {
      setGuestRegistrationStatus(StatusModalStates.ERROR);
      const parsedErrors = parseErrorMessages(error);
      setErrorMessages(parsedErrors);
      trackEvent(POSTHOG_EVENTS.INSTANT_CHECK_IN_FAILED, {
        failure_reason: 'guest_creation_error',
        participants_count: participants?.length,
      });
    },
    [trackEvent, participants],
  );

  const {mutateAsyncMultiple, isMutating: isMutatingMultipleGuests} =
    useMutateMultipleGuests({
      onSuccess: handleOnSuccessSaveMultipleGuests,
      onError: handleOnErrorSaveMultipleGuests,
    });

  const createGuests = useCallback(
    async ({
      customLeaderGuestId,
      customParticipants,
      guestsData,
    }: CreateGuestsArgs = {}) => {
      const selectedLeaderGuestId = customLeaderGuestId || leaderGuestId;
      const selectedParticipants = customParticipants || participants;

      const payload = selectedParticipants?.map(participant =>
        buildGuestInstantPayload({
          guestsData: guestsData ?? storedGuestsData,
          participant,
          reservationId: reservation?.id,
          leaderGuestId: selectedLeaderGuestId,
        }),
      );

      if (!payload?.length) return;

      setErrorMessages([]);
      openModal();
      setGuestRegistrationStatus(StatusModalStates.LOADING);
      void mutateAsyncMultiple(payload);
    },
    [
      participants,
      reservation?.id,
      openModal,
      mutateAsyncMultiple,
      storedGuestsData,
      leaderGuestId,
    ],
  );

  const closeModalAndReset = () => {
    setGuestRegistrationStatus(StatusModalStates.IDLE);
    setErrorMessages([]);
    closeModal();
  };

  const successSubtitle = useMemo(
    () => (
      <Trans
        i18nKey="count_of_guests_registered_modal_description"
        values={{count: totalRegisteredGuests}}
      />
    ),
    [totalRegisteredGuests],
  );

  const formattedErrorMessage =
    errorMessages.length > 0 ? errorMessages.join('\n\n') : t('something_went_wrong');

  useEffect(() => {
    return () => {
      clearTimeout(processTimeoutRef.current);
    };
  }, []);

  return {
    createGuests,
    isProcessing: isMutatingMultipleGuests,
    isModalOpen: isOpen,
    guestRegistrationStatus,
    successSubtitle,
    errorMessage: formattedErrorMessage,
    errorMessages,
    closeModalAndReset,
  };
};
